import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { User, Phone, Mail, Save, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { pushGA } from '../lib/utils';

export default function ProfilePage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setErr(null);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Sessão não encontrada');

        setEmail(user.email || '');

        const { data, error } = await supabase
          .from('users')
          .select('display_name, phone')
          .eq('id', user.id)
          .single();
        if (error) throw error;

        setDisplayName(data?.display_name || (user.user_metadata?.full_name ?? ''));
        setPhone(data?.phone || '');
      } catch (e: any) {
        setErr(e?.message ?? 'Falha ao carregar perfil');
      } finally {
        setLoading(false);
      }
    };
    load();
    pushGA('page_view', { page_title: 'Perfil do Usuário' });
  }, []);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null); 
    setErr(null);
    setSaving(true);
    
    try {
      if (!displayName.trim()) throw new Error('Informe seu nome');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Sessão expirada');

      // 1) Atualiza perfil (RLS garante que só edita a própria linha)
      const { error: upErr } = await supabase
        .from('users')
        .update({ 
          display_name: displayName.trim(), 
          phone: phone?.trim() || null 
        })
        .eq('id', user.id);
      if (upErr) throw upErr;

      // 2) Sincroniza display name no Auth (aparece no Dashboard)
      const { error: metaErr } = await supabase.auth.updateUser({
        data: { full_name: displayName.trim() },
      });
      if (metaErr) throw metaErr;

      setMsg('Perfil atualizado com sucesso!');
      pushGA('profile_updated', { has_phone: !!phone?.trim() });
      
      // Remove mensagem de sucesso após 3 segundos
      setTimeout(() => setMsg(null), 3000);
    } catch (e: any) {
      setErr(e?.message ?? 'Falha ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-darling-lilac/10 dark:bg-darling-lilac/20 mb-4">
            <Loader2 className="h-8 w-8 text-darling-lilac animate-spin" />
          </div>
          <p className="text-soft-black/70 dark:text-light-gray/80 font-exo">
            Carregando perfil...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-darling-lilac/10 dark:bg-darling-lilac/20">
            <User className="h-6 w-6 text-darling-lilac" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-exo font-black text-soft-black dark:text-white">
              Meu Perfil
            </h1>
            <p className="text-soft-black/70 dark:text-light-gray/80 font-exo">
              Gerencie suas informações pessoais
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-soft-black rounded-2xl border border-light-gray-alt dark:border-deep-purple/50 shadow-lg overflow-hidden"
      >
        <div className="p-6">
          <form onSubmit={save} className="space-y-6">
            {/* Email (readonly) */}
            <div>
              <label className="block text-sm font-exo font-medium text-soft-black dark:text-white mb-2">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-soft-black/40 dark:text-light-gray/50" />
                </div>
                <input
                  type="email"
                  disabled
                  value={email}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-light-gray/50 dark:bg-deep-purple/20 text-soft-black/60 dark:text-light-gray/60 font-exo cursor-not-allowed"
                />
              </div>
              <p className="text-xs text-soft-black/50 dark:text-light-gray/60 font-exo mt-1">
                O email não pode ser alterado
              </p>
            </div>

            {/* Display Name */}
            <div>
              <label className="block text-sm font-exo font-medium text-soft-black dark:text-white mb-2">
                Nome completo
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-soft-black/40 dark:text-light-gray/50" />
                </div>
                <input
                  type="text"
                  required
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black text-soft-black dark:text-white font-exo focus:ring-2 focus:ring-darling-lilac/20 focus:border-darling-lilac/50 transition-all"
                  placeholder="Seu nome completo"
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-exo font-medium text-soft-black dark:text-white mb-2">
                Telefone
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-soft-black/40 dark:text-light-gray/50" />
                </div>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black text-soft-black dark:text-white font-exo focus:ring-2 focus:ring-darling-lilac/20 focus:border-darling-lilac/50 transition-all"
                  placeholder="+55 (11) 99999-9999"
                />
              </div>
              <p className="text-xs text-soft-black/50 dark:text-light-gray/60 font-exo mt-1">
                O telefone precisa conter codigo do pais e DDD
              </p>
            </div>

            {/* Messages */}
            {err && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50"
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <p className="text-sm text-red-600 dark:text-red-400 font-exo">
                    {err}
                  </p>
                </div>
              </motion.div>
            )}

            {msg && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 rounded-lg bg-ocean-water/10 dark:bg-ocean-water/20 border border-ocean-water/30 dark:border-ocean-water/50"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-ocean-water" />
                  <p className="text-sm text-ocean-water font-exo">
                    {msg}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-darling-lilac hover:bg-deep-purple disabled:bg-darling-lilac/50 text-white font-exo font-semibold transition-all duration-200 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Salvar perfil
                </>
              )}
            </button>
          </form>
        </div>
      </motion.div>

      {/* Info Card */}
      <div className="mt-6 p-4 rounded-xl bg-light-gray/50 dark:bg-deep-purple/20 border border-light-gray-alt dark:border-deep-purple/30">
        <h3 className="text-sm font-exo font-semibold text-soft-black dark:text-white mb-2">
          Sobre suas informações
        </h3>
        <div className="text-xs text-soft-black/60 dark:text-light-gray/70 font-exo space-y-1">
          <p>• Guardamos seus dados só aqui no app.</p>
          <p>• Você é a única pessoa que pode mudar esses dados.</p>
        </div>
      </div>
    </div>
  );
}